import { Box, Heading, Skeleton, Text, useToast } from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import {
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import { CreditFacilityGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { CalculationTerminologiesTable } from './CalculationTerminologiesTable';
import { getInitialCalcTermsRows } from './calculationTerminologiesUtils';
import { colors } from 'packages/common-frontend/colors';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery } from 'packages/creditAgreements/requests/requests.gql.generated';
import { parseCreditAgreementFullArtifact } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';

// Exports
type CalculationTerminologiesContainerProps = {
  organizationId: string;
  creditFacilities: Pick<CreditFacilityGQLType, 'id' | 'slug'>[];
};
export const CalculationTerminologiesContainer = ({
  organizationId,
  creditFacilities,
}: CalculationTerminologiesContainerProps) => {
  const flags = useFeatureFlags();
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: creditAgreementFullArtifactsData,
    loading: isCreditAgreementFullArtifactsLoading,
  } = useGetCurrentCreditAgreementFullArtifactsForOrganizationQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
    variables: {
      organizationId,
      creditFacilitySlugsToHide: flags?.creditFacilitySlugsToHide ?? [],
    },
  });

  if (
    !creditAgreementFullArtifactsData ||
    isCreditAgreementFullArtifactsLoading
  ) {
    return <Skeleton height="700px" />;
  }

  const {
    organization: { currentCreditAgreementArtifacts },
  } = creditAgreementFullArtifactsData;

  let parsedCreditAgreementFullArtifacts = null;

  try {
    parsedCreditAgreementFullArtifacts = _.map(
      currentCreditAgreementArtifacts,
      (creditAgreementFullArtifact) => {
        const { creditAgreement, id } = creditAgreementFullArtifact;
        const parsedCreditAgreement = parseCreditAgreementFullArtifact(
          creditAgreement,
        );
        finleyAssert(
          parsedCreditAgreement,
          `Expect creditAgreementFullArtifact to exist for creditAgreementId ${id}`,
        );
        return parsedCreditAgreement;
      },
    );
  } catch (err) {
    const errorToastTitle = `Error parsing credit agreement artifacts for organization ${organizationId}`;
    toast({
      ...errorToastConfig,
      title: errorToastTitle,
      id: 'errorParsingCreditAgreement',
    });
    navigate('/');
  }

  const calcTermsTableRows = getInitialCalcTermsRows({
    creditAgreementArtifacts: parsedCreditAgreementFullArtifacts,
    creditFacilities,
  });

  const orderedCalcTermsTableRows = _.orderBy(
    calcTermsTableRows,
    'termKey',
    'asc',
  );

  return (
    <Box>
      <Heading as="h2" mb="1rem">
        Calculation Terminologies
      </Heading>
      <Text fontSize="16px" color={colors.black[70]} mb="32px">
        Finley&apos;s definition of the Calculation Terminologies.
      </Text>

      <CalculationTerminologiesTable
        calculationTerminologiesTableRows={orderedCalcTermsTableRows}
      />
    </Box>
  );
};
